// Leverantörer (admin mockup v2 §3): facts about one supplier's labels, their codes mapped to
// our articles, and a worked resolution across site, profile and supplier.

import { useEffect, useMemo, useState } from "react";
import type { CaptureOverrides, SupplierLabelFacts } from "@trail/core";
import { Admin, Api, type BundleGtin, type BundleProduct, type ReferenceBundle, type SupplierSummary } from "../api/client.js";
import { t, uomLabel } from "../i18n.js";
import { DetailBar, useDetailHistory } from "./settings-ui.jsx";
import { buildRegistry, rulesFor } from "../receive/registry.js";

type Req = "inherit" | "required" | "optional" | "forbidden";
const ATTRS = ["bestBefore", "lot", "expiry", "serial", "netContent"] as const;
const CONTENT = [{ ai: "3110", uom: "MTR" }, { ai: "3100", uom: "KGM" }, { ai: "30", uom: "EA" }];

export function Suppliers() {
  const [list, setList] = useState<SupplierSummary[] | null>(null);
  const [bundle, setBundle] = useState<ReferenceBundle | null>(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<SupplierSummary | null>(null);
  useDetailHistory(!!sel, () => { setSel(null); });
  const [facts, setFacts] = useState<SupplierLabelFacts | null>(null);
  const [prefixes, setPrefixes] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  const load = () => {
    Admin.suppliers().then(setList).catch(() => setList([]));
    Api.bundle().then(setBundle).catch(() => setBundle(null));
  };
  useEffect(load, []);
  useEffect(() => {
    setFacts(sel ? sel.labelFacts : null);
    setPrefixes(sel ? sel.labelFacts.knownPrefixes.join(" · ") : "");
    setMsg(null);
  }, [sel]);
  const registry = useMemo(() => (bundle ? buildRegistry(bundle) : null), [bundle]);

  const needle = q.trim().toLowerCase();
  const rows = (list ?? []).filter((s) => !needle || s.name.toLowerCase().includes(needle) || (s.key ?? "").toLowerCase().includes(needle) || (s.shortName ?? "").toLowerCase().includes(needle)).slice(0, 300);
  const products: { p: BundleProduct; g: BundleGtin | null; article: string | null }[] = sel && bundle
    ? bundle.products
        .filter((p) => p.gtins.some((g) => g.supplierPartyId === sel.id) || p.supplierArticles.some((a) => a.supplierPartyId === sel.id))
        .map((p) => ({ p, g: p.gtins.find((g) => g.supplierPartyId === sel.id) ?? null, article: p.supplierArticles.find((a) => a.supplierPartyId === sel.id)?.article ?? null }))
    : [];

  const save = async () => {
    if (!sel || !facts) return;
    const knownPrefixes = prefixes.split(/[^0-9]+/).filter((x) => x.length >= 4);
    try {
      await Admin.putSupplierLabelFacts(sel.id, { ...facts, knownPrefixes });
      setMsg(t("adm.sup.saved"));
      load();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  };
  const setReq = (k: (typeof ATTRS)[number], v: Req) => {
    if (!facts) return;
    const next: CaptureOverrides = { ...facts.captureOverrides };
    if (v === "inherit") delete next[k];
    else next[k] = v;
    setFacts({ ...facts, captureOverrides: next });
  };
  const reqLabel = (v: string) => (v === "required" ? t("adm.sup.req.required") : v === "optional" ? t("adm.sup.req.optional") : v === "forbidden" ? t("adm.sup.req.forbidden") : t("adm.sup.req.inherit"));
  const attrLabel = (k: string) => (k === "bestBefore" ? t("adm.sup.attr.bestBefore") : k === "lot" ? t("adm.sup.attr.lot") : k === "expiry" ? t("adm.sup.attr.expiry") : k === "serial" ? t("adm.sup.attr.serial") : t("adm.sup.attr.netContent"));
  const overrideText = (g: BundleGtin | null) => {
    if (!g?.captureOverrides) return "—";
    const parts = Object.entries(g.captureOverrides).filter(([, v]) => v !== undefined).map(([k, v]) => `${attrLabel(k)}: ${reqLabel(String(v))} ${facts?.captureOverrides[k as keyof CaptureOverrides] === v ? t("adm.sup.fromSupplier") : t("adm.sup.fromLink")}`);
    return parts.length ? parts.join(" · ") : "—";
  };

  // The worked resolution: best-before on the first linked article with a supplier GTIN.
  const example = products.find((x) => x.g) ?? null;
  const rules = example && registry ? rulesFor(registry, example.p, example.g) : null;
  const siteDefault = registry?.siteDefault ?? null;
  const profileCfg = rules && registry ? registry.bundle.profiles.find((p) => p.name === rules.profileName)?.config ?? siteDefault?.config ?? null : null;
  const missing = registry?.bundle.siteConfig?.receiving.missingRequired ?? "must-enter";

  return (
    <>
      <div className="adm-top">
        <div><h1>{t("adm.suppliers")}</h1><div className="crumb">{list ? t("adm.sup.sub", { n: list.length, linked: list.filter((s) => s.articles > 0).length }) : ""}</div></div>
        <div className="right"><input className="ain" style={{ height: 36, width: 320, borderRadius: 10, border: "1px solid var(--line)", background: "var(--bg)", padding: "0 12px", font: "inherit", color: "var(--text)" }} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("adm.sup.search")} autoFocus /></div>
      </div>
      <div className="adm-content">
        <div className={`adm-cols${!!sel ? " detail-open" : ""}`}>
          <div className="card adm-grow">
            <div className="bd flush">
              <table className="at">
                <thead><tr><th>{t("adm.sup.col.key")}</th><th>{t("adm.sup.col.name")}</th><th>{t("adm.sup.col.country")}</th><th>GLN</th><th className="num">{t("adm.sup.col.articles")}</th></tr></thead>
                <tbody>
                  {rows.map((s) => (
                    <tr key={s.id} className={`click${sel?.id === s.id ? " sel" : ""}`} onClick={() => setSel(s)}>
                      <td className="id">{s.key ?? "—"}</td>
                      <td>{s.name}{s.shortName && s.shortName !== s.name ? <span className="mut"> · {s.shortName}</span> : null}</td>
                      <td className="mut">{s.country ?? "—"}</td>
                      <td className="id mut">{s.gln ?? "—"}</td>
                      <td className="num">{s.articles || ""}</td>
                    </tr>
                  ))}
                  {list && list.length > rows.length && <tr><td colSpan={5} className="mut">{t("rows.more", { n: list.length - rows.length })}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          {sel && facts && (
            <div className="adm-w580 adm-detail adm-detail-only" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {!!sel && <DetailBar title={sel ? sel.name : ""} sub={undefined} onBack={() => { setSel(null); }} />}
              <div className="label" style={{ background: "var(--panel)" }}>
                <div className="head"><b>{sel.name}</b><span>{[sel.key, sel.country, sel.gln ? `GLN ${sel.gln}` : t("adm.sup.noGln")].filter(Boolean).join(" · ")}</span></div>
              </div>
              <div className="card">
                <div className="hd">{t("adm.sup.facts")} <span className="sub">{msg ?? t("adm.sup.facts.sub")}</span><button type="button" className="abtn primary sm" onClick={() => void save()}>{t("settings.save")}</button></div>
                <div className="bd aform" style={{ gridTemplateColumns: "130px 1fr" }}>
                  <span>{t("adm.sup.barcode")}</span>
                  <input className="ain" value={facts.barcode ?? ""} onChange={(e) => setFacts({ ...facts, barcode: e.target.value || null })} placeholder={t("adm.sup.barcode.ph")} />
                  <span>{t("adm.sup.contentFrom")}</span>
                  <div className="achips">
                    {CONTENT.map((c) => <button key={c.ai} type="button" className={`achip${facts.quantityFrom?.ai === c.ai ? " on" : ""}`} onClick={() => setFacts({ ...facts, quantityFrom: c })}>AI {c.ai} · {uomLabel(c.uom)}</button>)}
                    <button type="button" className={`achip${facts.quantityFrom ? "" : " on"}`} onClick={() => setFacts({ ...facts, quantityFrom: null })}>{t("adm.sup.contentLevel")}</button>
                  </div>
                  {ATTRS.map((k) => (
                    <SegRow key={k} label={attrLabel(k)} value={(facts.captureOverrides[k] ?? "inherit") as Req} onChange={(v) => setReq(k, v)} reqLabel={reqLabel} />
                  ))}
                  <span>{t("adm.sup.lotGrammar")}</span>
                  <input className="ain" value={facts.lotGrammar ?? ""} onChange={(e) => setFacts({ ...facts, lotGrammar: e.target.value || null })} placeholder={t("adm.sup.lotGrammar.ph")} />
                  <span>{t("adm.sup.prefixes")}</span>
                  <input className="ain mono" value={prefixes} onChange={(e) => setPrefixes(e.target.value)} placeholder="9859160 · 5412340" />
                </div>
              </div>
              <div className="card">
                <div className="hd">{t("adm.sup.articles", { name: sel.shortName ?? sel.name })} <span className="sub">{products.length}</span></div>
                <div className="bd flush">
                  <table className="at">
                    <thead><tr><th>{t("adm.sup.col.article")}</th><th>{t("adm.pr.col.name")}</th><th>{t("adm.sup.col.gtin")}</th><th>{t("adm.sup.col.deviates")}</th></tr></thead>
                    <tbody>
                      {products.length === 0 && <tr><td colSpan={4} className="mut">—</td></tr>}
                      {products.slice(0, 100).map(({ p, g, article }) => (
                        <tr key={p.id}><td className="id">{article ?? "—"}</td><td>{p.name}<div className="mut" style={{ fontSize: 12 }}>{p.sku}</div></td><td className="id">{g?.gtin ?? "—"}<div className="mut" style={{ fontSize: 12, fontFamily: "var(--sans)" }}>{g?.quantityFrom ? `AI ${g.quantityFrom.ai} · ${uomLabel(g.quantityFrom.uom)}` : t("adm.sup.contentLevel")}</div></td><td className="mut">{overrideText(g)}</td></tr>
                      ))}
                      {products.length > 100 && <tr><td colSpan={4} className="mut">{t("rows.more", { n: products.length - 100 })}</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
              {example && rules && siteDefault && profileCfg && (
                <div className="card">
                  <div className="hd">{t("adm.sup.resolve")} <span className="sub">{t("adm.sup.resolve.sub", { sku: example.p.sku })}</span></div>
                  <div className="bd flush">
                    <table className="at">
                      <thead><tr><th>{t("adm.sup.level")}</th><th>{t("adm.sup.says")}</th><th></th></tr></thead>
                      <tbody>
                        <tr><td><span className="lvl">{t("adm.sup.lvl.site")}</span> {bundle?.sites[0]?.name ?? ""}</td><td>{t("adm.sup.attr.bestBefore")} {reqLabel(siteDefault.config.capture.bestBefore)}</td><td className="mut">{siteDefault.name}</td></tr>
                        <tr><td><span className="lvl">{t("adm.sup.lvl.profile")}</span> {rules.profileName} · {t(`adm.pr.profileFrom.${rules.profileFrom}` as "adm.pr.profileFrom.site")}</td><td>{t("adm.sup.attr.bestBefore")} <b>{reqLabel(profileCfg.capture.bestBefore)}</b></td><td className="mut">{rules.profileFrom === "site" ? t("adm.sup.isDefault") : t("adm.sup.replacesSite")}</td></tr>
                        <tr><td><span className="lvl">{t("adm.sup.lvl.supplier")}</span> {sel.shortName ?? sel.name}</td><td>{rules.supplierOverrides.includes("bestBefore") ? `${t("adm.sup.attr.bestBefore")} ${reqLabel(rules.capture.bestBefore)}` : t("adm.sup.noDeviation")}</td><td className="mut">{rules.supplierOverrides.includes("bestBefore") ? t("adm.sup.replacesProfile") : ""}</td></tr>
                        <tr><td><b>{t("adm.sup.result")}</b></td><td colSpan={2}><b>{t("adm.sup.attr.bestBefore")} {reqLabel(rules.capture.bestBefore)}{rules.capture.bestBefore === "required" ? ` · ${missing === "must-enter" ? t("adm.sup.result.mustEnter") : t("adm.sup.result.allowWithout")}` : ""}</b></td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function SegRow({ label, value, onChange, reqLabel }: { label: string; value: Req; onChange: (v: Req) => void; reqLabel: (v: string) => string }) {
  return (
    <>
      <span>{label}</span>
      <div className="seg">
        {(["inherit", "required", "optional", "forbidden"] as const).map((v) => <button key={v} type="button" className={value === v ? "on" : ""} onClick={() => onChange(v)}>{reqLabel(v)}</button>)}
      </div>
    </>
  );
}
